import { activeProject, postProductEvents, workspaceSlug } from './api'

type ProductDevice = 'mobile' | 'tablet' | 'desktop' | 'unknown'
type ProductProperty = string | number | boolean | null

export type ProductProperties = Record<string, ProductProperty>

interface ProductEvent {
  name: string
  surface: string | null
  device: ProductDevice
  properties: ProductProperties
}

const MAX_BATCH = 25
const EVENT_NAME = /^[a-z][a-z0-9_.-]{0,79}$/
const SURFACE_NAME = /^[a-z][a-z0-9_./-]{0,79}$/
const PROPERTY_NAME = /^[a-z][a-z0-9_.-]{0,39}$/

let queue: ProductEvent[] = []
let timer: number | undefined
let hooked = false
let buttonsHooked = false

function optedOut(): boolean {
  if (typeof navigator === 'undefined') return true
  const privacy = navigator as Navigator & { globalPrivacyControl?: boolean }
  return navigator.doNotTrack === '1' || privacy.globalPrivacyControl === true
}

function deviceClass(): ProductDevice {
  if (typeof navigator === 'undefined') return 'unknown'
  const ua = navigator.userAgent
  if (/ipad|tablet/i.test(ua)) return 'tablet'
  if (/mobi|android|iphone/i.test(ua)) return 'mobile'
  return 'desktop'
}

function cleanProperties(input: ProductProperties | undefined): ProductProperties {
  if (!input) return {}
  const out: ProductProperties = {}
  for (const [key, value] of Object.entries(input)) {
    if (!PROPERTY_NAME.test(key)) continue
    out[key] = typeof value === 'string' ? value.slice(0, 120) : value
  }
  return JSON.stringify(out).length <= 1200 ? out : {}
}

function cleanControlName(raw: string | null | undefined): string | null {
  const cleaned = raw
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80)
  if (!cleaned) return null
  return /^[a-z]/.test(cleaned) ? cleaned : `control_${cleaned}`
}

function explicitControlName(control: HTMLElement): string | null {
  const tracked = cleanControlName(control.dataset['track'])
  if (tracked) return tracked
  if (control.hasAttribute('data-switcher')) return 'switcher.toggle'
  if (control.hasAttribute('data-site')) return 'switcher.site'
  if (control.hasAttribute('data-workspace')) return 'switcher.workspace'
  if (control.hasAttribute('data-new-site')) return 'switcher.new_site'
  if (control.hasAttribute('data-new-site-locked')) return 'switcher.new_site_locked'

  const nav = cleanControlName(control.dataset['nav'])
  if (nav) return `nav.${nav}`
  return null
}

function readableControlName(control: HTMLElement): string {
  const stable = explicitControlName(control)
  if (stable) return stable

  const labelled =
    control.getAttribute('aria-label') ??
    control.querySelector('.sr-only')?.textContent ??
    control.getAttribute('data-tip') ??
    control.getAttribute('title') ??
    control.textContent
  return cleanControlName(labelled) ?? 'button'
}

function shouldTrackButton(control: HTMLElement): boolean {
  if (control.closest('[data-analytics-ignore]')) return false
  if (control.closest('.ProseMirror, .cm-editor, [contenteditable="true"]')) return false
  if (control.closest('.nav-page') && !control.closest('.rowmenu')) return false
  return true
}

function eventHeaders(): HeadersInit | null {
  const workspace = workspaceSlug()
  const project = activeProject()
  if (!workspace || !project) return null
  return {
    'content-type': 'application/json',
    'x-workspace': workspace,
    'x-project': project,
  }
}

function hookFlush(): void {
  if (hooked || typeof window === 'undefined' || typeof document === 'undefined') return
  hooked = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushProductEvents()
  })
  window.addEventListener('pagehide', () => flushProductEvents())
}

function scheduleFlush(): void {
  if (timer !== undefined || typeof window === 'undefined') return
  timer = window.setTimeout(() => {
    timer = undefined
    void flushProductEvents()
  }, 750)
}

export function trackProductEvent(
  name: string,
  options: { surface?: string | null; properties?: ProductProperties } = {},
): void {
  if (optedOut()) return
  if (!EVENT_NAME.test(name)) return
  const surface = options.surface?.trim() ?? ''
  if (surface && !SURFACE_NAME.test(surface)) return

  hookFlush()
  queue.push({
    name,
    surface: surface || null,
    device: deviceClass(),
    properties: cleanProperties(options.properties),
  })
  if (queue.length >= MAX_BATCH) void flushProductEvents()
  else scheduleFlush()
}

export function installProductButtonTracking(surfaceOf: () => string | null): () => void {
  if (buttonsHooked || typeof document === 'undefined') return () => undefined
  buttonsHooked = true
  const onClick = (event: MouseEvent): void => {
    const target = event.target instanceof Element ? event.target : null
    const control = target?.closest('button, [role="button"]')
    if (!(control instanceof HTMLElement)) return
    if (!shouldTrackButton(control)) return

    trackProductEvent('button.clicked', {
      surface: surfaceOf(),
      properties: {
        control: readableControlName(control),
        pressed: control.getAttribute('aria-pressed') === 'true',
      },
    })
  }
  document.addEventListener('click', onClick, { capture: true })
  return () => {
    buttonsHooked = false
    document.removeEventListener('click', onClick, { capture: true })
  }
}

export async function flushProductEvents(): Promise<void> {
  if (queue.length === 0) return
  const headers = eventHeaders()
  if (!headers) return
  const events = queue.splice(0, MAX_BATCH)
  try {
    const accepted = await postProductEvents(JSON.stringify({ events }), headers)
    if (!accepted) queue = events.concat(queue).slice(0, MAX_BATCH)
  } catch {
    queue = events.concat(queue).slice(0, MAX_BATCH)
  }
}
